import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/components/feature/ContactForm";
import { usePageSeo, SITE_ORIGIN } from "@/hooks/usePageSeo";
import { site, values, testimonials } from "@/mocks/site";

const positioning = [
  {
    icon: "ri-radar-line",
    title: "热点聚合",
    desc: "把分散在各平台的消息收拢成一条条完整瓜线，附上背景与关键节点。",
  },
  {
    icon: "ri-list-check-2",
    title: "时间线梳理",
    desc: "每个话题按时间排序，反转来了就更新，让你一眼看清来龙去脉。",
  },
  {
    icon: "ri-chat-smile-3-line",
    title: "聊天室讨论",
    desc: "按兴趣分房间，边吃边聊，把一个人的围观变成一群人的热闹。",
  },
  {
    icon: "ri-book-marked-line",
    title: "围观方法论",
    desc: "把吃瓜经验沉淀成可复用的判断方法，让每一次围观都更清醒。",
  },
];

const principles = [
  "不发布未经确认的信息，存疑内容会明确标注。",
  "不点名施压，不对当事人做人身评价。",
  "每一条瓜线都保留修订记录，更正时公开说明。",
  "尊重不同观点，给争论留出独立空间。",
  "不做标题党，标题必须与正文内容一致。",
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 - 诱惑吃瓜",
      url: `${SITE_ORIGIN}/about`,
      inLanguage: "zh-CN",
      description: "了解诱惑吃瓜的网站定位、发展理念、内容原则与联系渠道。",
    },
    {
      "@type": "Organization",
      name: site.name,
      url: SITE_ORIGIN,
      slogan: site.slogan,
      email: site.email,
      description: site.describe,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "关于我们" },
      ],
    },
  ],
};

export default function About() {
  usePageSeo({
    title: "关于我们 - 网站定位与内容原则 | 诱惑吃瓜",
    description:
      "诱惑吃瓜是一个聚焦娱乐话题与围观方法论的讨论社区。这里介绍我们的品牌定位、发展理念、内容原则与联系方式，欢迎理性围观、友善讨论。",
    keywords: "关于诱惑吃瓜,网站定位,内容原则,围观礼仪,联系我们",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 pt-16 md:pt-20">
        <section className="w-full px-4 md:px-10 pt-8 md:pt-12 pb-10 md:pb-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 bg-primary-100/70 px-3 py-1 rounded-full">
                <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
                关于 {site.name}
              </span>
              <h1 className="mt-4 text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                让围观这件事，
                <br />
                变得更有分寸
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl">
                {site.name}诞生于一个很简单的想法：大家都爱看热闹，但很少有地方教你怎么看得明白。我们把热点整理成瓜线，把经验写成方法论，也把聊天室留给所有想一起聊两句的人。
              </p>
              <div className="mt-7 flex flex-wrap gap-3 text-sm">
                <span className="px-4 py-2 rounded-md bg-background-100 border border-background-200 text-foreground-700">
                  {site.slogan}
                </span>
                <span className="px-4 py-2 rounded-md bg-background-100 border border-background-200 text-foreground-700">
                  {site.tagline}
                </span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-lg overflow-hidden bg-background-200 aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20of%20a%20cozy%20gathering%20circle%20of%20lamp%20lights%20and%20flowing%20amber%20ribbons%20over%20terracotta%20paper%20texture%2C%20editorial%20minimal%20style%2C%20no%20people%2C%20no%20text%2C%20soft%20harmonious%20palette&width=1200&height=900&seq=about-guatan-01&orientation=landscape"
                  alt="诱惑吃瓜 社区氛围主题插画"
                  title="诱惑吃瓜 关于我们"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-16 md:py-24 bg-background-100/60">
          <SectionHeading
            eyebrow="网站定位"
            title="我们在做的四件事"
            desc="不做信息的无限搬运工，而是做那个帮你把线索串起来的人。"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {positioning.map((item) => (
              <div key={item.title} className="p-6 rounded-lg bg-background-50 border border-background-200">
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                  <i className={`${item.icon} w-6 h-6 flex items-center justify-center`}></i>
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="发展理念"
                title="有趣，但要有底线"
                desc="我们希望这里热闹，也希望这里体面。热闹让人愿意留下，体面让人愿意长久留下来。"
              />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {values.map((item) => (
                <div key={item.title} className="p-6 rounded-lg border border-background-200 bg-background-50">
                  <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center text-xl">
                    <i className={`${item.icon} w-6 h-6 flex items-center justify-center`}></i>
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-16 md:py-24 bg-secondary-950">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-accent-300 bg-accent-500/15 px-3 py-1 rounded-full">
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
                内容原则
              </span>
              <h2 className="mt-4 text-2xl md:text-3xl font-bold text-background-50 leading-snug">
                五条我们一直在守的规矩
              </h2>
              <p className="mt-4 text-sm md:text-base text-secondary-200 leading-relaxed">
                规矩不是为了限制讨论，而是为了让讨论值得被认真对待。发现内容有误时，欢迎通过页面底部的反馈入口告诉我们。
              </p>
            </div>
            <ol className="lg:col-span-7 space-y-3">
              {principles.map((text, index) => (
                <li
                  key={text}
                  className="flex items-start gap-4 p-5 rounded-lg bg-background-50/5 border border-background-50/15"
                >
                  <span className="w-8 h-8 shrink-0 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm md:text-base text-secondary-100 leading-relaxed">{text}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-16 md:py-24">
          <SectionHeading
            eyebrow="用户口碑"
            title="他们眼里的诱惑吃瓜"
            desc="来自不同房间、不同习惯的围观者，说说他们为什么留了下来。"
            align="center"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="p-6 rounded-lg bg-background-50 border border-background-200">
                <blockquote className="text-sm text-foreground-700 leading-relaxed">“{item.text}”</blockquote>
                <figcaption className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={item.avatar}
                      alt={`${item.name} 的头像`}
                      title={`${item.name} 的评价`}
                      className="w-full h-full object-cover"
                    />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact-us" className="w-full px-4 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="联系我们"
                title="有线索，或者有建议？"
                desc="无论是内容纠错、补充时间点，还是对围观礼仪的建议，都可以直接告诉我们。"
              />
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">邮箱</p>
                    <a
                      href={`mailto:${site.email}`}
                      className="text-sm text-primary-600 hover:text-primary-700 break-all"
                    >
                      {site.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className="ri-chat-3-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">聊天室</p>
                    <p className="text-sm text-foreground-600">主瓜田 24 小时开放，随时进来聊两句。</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className="ri-time-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">回复时间</p>
                    <p className="text-sm text-foreground-600">通常 1–2 个工作日内回复，热点期间会更快。</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <h3 className="text-lg font-semibold text-foreground-950">给我们留言</h3>
                <p className="mt-2 text-sm text-foreground-500 mb-6">带 * 的为必填项，写清楚时间点会更好处理。</p>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}